/*
  Theme resolution — the React copy.

  The second copy is the blocking inline script in public/index.html, which has to exist
  because a CRA bundle runs after the stylesheet and cannot stamp the attribute before
  paint (spec §3.2). The two copies cannot import from each other, so they must be kept
  in step by hand. Every rule below is mirrored there:

    - localStorage read and write are wrapped, so a private window or blocked site data
      still renders
    - a stored value that is not "light"/"dark" is ignored and treated as no value
    - with no stored value the system preference decides, and matchMedia().matches
      false — a light-preference OR a no-preference browser — resolves to light
    - if matchMedia itself throws we resolve to dark, matching the bare :root CSS so
      there is no flash
    - if window.matchMedia is simply absent we resolve to light, as the inline script's
      `!!window.matchMedia && ...` does

  Since the skin landed there is a second thing the two copies share: the theme-color
  pair is now indexed by skin as well as by mode, and an unrecognised data-skin is
  normalised to "navy" in both. Teaching only one copy about the skin is the flash this
  design exists to avoid, so treat them as one file in two languages.
*/

import { SKIN, Skin } from "skin";

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "pcg-pop-report-theme";

export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/*
  The phone browser's chrome colour, and it depends on BOTH axes.

  It is the colour the address bar and the task switcher paint around the page, so it
  has to be the page's own colour or the frame sits visibly wrong against it — most
  obviously on the phone this report is mostly read on. Gilded's page is a warm
  near-black, not navy's blue-black, so the pair has to move with the skin.

  Navy's row is untouched, including the fact that its dark value (#111111) is not its
  page token (#0B0E14). That is a pre-existing mismatch; correcting it here would mean
  the navy revert no longer returns exactly what is live today, so it stays. Gilded's
  row uses the real page colours.

  Kept in step BY HAND with the same table in public/index.html's inline script — the
  two cannot import from each other, exactly as the theme resolution below cannot.
*/
export const THEME_COLORS_BY_SKIN: Record<Skin, Record<Theme, string>> = {
  navy: {
    light: "#F4F5F7",
    dark: "#111111",
  },
  gilded: {
    light: "#F4EFE6",
    dark: "#0C0A08",
  },
  /* The event skin's chrome: #0B0E14 is its navy header (the storefront's #221F27 until 2026-10-20). */
  event: {
    light: "#FFFFFF",
    dark: "#0B0E14",
  },
};

/*
  What a visitor with nothing stored sees first, per skin.

  Navy and gilded follow the OS preference, as they always have. The event skin opens
  DARK for everyone (Robert, 2026-10-19: "toggle, default dark") because dark is the look
  it was designed in — the event guide pages are dark-only — and white is the opt-in.
  A visitor's own toggle still wins over either, and once the default is a fixed "dark"
  the OS preference has no vote at all, at load or later.

  Mirrored by hand in public/index.html's inline script, like the colour table above.
*/
export type ThemeDefault = Theme | "system";

export const DEFAULT_THEME_BY_SKIN: Record<Skin, ThemeDefault> = {
  navy: "system",
  gilded: "system",
  event: "dark",
};

/* The pair this build actually ships. */
export const THEME_COLORS: Record<Theme, string> = THEME_COLORS_BY_SKIN[SKIN];

export const isTheme = (value: unknown): value is Theme =>
  value === "light" || value === "dark";

/*
  A stored value that is not one of the two is ignored, not repaired — the user never
  chose it, so it must not win over the system preference.
*/
export const getStoredTheme = (): Theme | null => {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch (e) {
    /* private window or blocked site data — treat it as no stored value */
  }
  return isTheme(stored) ? stored : null;
};

/* Returns whether the choice actually persisted; a blocked store is not an error here. */
export const storeTheme = (theme: Theme): boolean => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    return true;
  } catch (e) {
    return false;
  }
};

export const getSystemTheme = (): Theme => {
  let prefersDark = false;
  try {
    prefersDark = !!window.matchMedia && window.matchMedia(DARK_SCHEME_QUERY).matches;
  } catch (e) {
    /* no usable matchMedia — the bare :root default is dark, so match it */
    prefersDark = true;
  }
  return prefersDark ? "dark" : "light";
};

const followsSystem = (skin: Skin) => DEFAULT_THEME_BY_SKIN[skin] === "system";

const defaultTheme = (skin: Skin): Theme => {
  const fallback = DEFAULT_THEME_BY_SKIN[skin];
  return fallback === "system" ? getSystemTheme() : fallback;
};

export const resolveTheme = (skin: Skin = SKIN): Theme =>
  getStoredTheme() ?? defaultTheme(skin);

/*
  Stamps the skin. In a browser this is a no-op that repaints nothing: public/index.html
  already carries data-skin in the served bytes, which is the whole point of putting it
  there. It exists so the React copy is complete on its own — jsdom, and any future host
  that does not use CRA's HTML template, still get the attribute — and so that a build
  where %REACT_APP_SKIN% failed to interpolate ends up with the same normalised value in
  the DOM that src/skin.ts resolved.
*/
export const applySkin = (skin: Skin = SKIN) => {
  document.documentElement.setAttribute("data-skin", skin);
};

export const applyTheme = (theme: Theme) => {
  document.documentElement.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", THEME_COLORS[theme]);
  }
};

/*
  Safari before 14 only has the deprecated addListener/removeListener pair, and it is in
  the browserslist production target, so both shapes are handled.
*/
type LegacyMediaQueryList = MediaQueryList & {
  addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
};

const noop = () => {};

/* Subscribes to OS preference changes. Returns the unsubscribe. */
export const watchSystemTheme = (onChange: (theme: Theme) => void) => {
  let query: LegacyMediaQueryList;
  try {
    if (!window.matchMedia) {
      return noop;
    }
    query = window.matchMedia(DARK_SCHEME_QUERY) as LegacyMediaQueryList;
  } catch (e) {
    return noop;
  }

  const listener = (event: MediaQueryListEvent) => {
    onChange(event.matches ? "dark" : "light");
  };

  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }
  if (typeof query.addListener === "function") {
    query.addListener(listener);
    return () => {
      if (query.removeListener) {
        query.removeListener(listener);
      }
    };
  }
  return noop;
};

export interface ThemeController {
  getTheme: () => Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  subscribe: (listener: (theme: Theme) => void) => () => void;
  stop: () => void;
}

/*
  Holds the resolved theme and owns the matchMedia subscription. All of the behaviour
  lives here rather than in the hook so it is testable as a plain function — component
  tests do not run in this repo (RTL 13 against React 17, spec §2).
*/
export const createThemeController = (skin: Skin = SKIN): ThemeController => {
  let theme = resolveTheme(skin);
  /*
    Once the visitor has chosen, the OS no longer gets a vote (spec §3.2). This is
    tracked separately from the stored value so a choice made while storage is blocked
    still silences the listener for the life of the tab.
  */
  let chosen = getStoredTheme() !== null;
  const listeners: Array<(theme: Theme) => void> = [];

  applySkin(skin);
  applyTheme(theme);

  const publish = (next: Theme) => {
    if (next === theme) {
      return;
    }
    theme = next;
    applyTheme(theme);
    listeners.slice().forEach((listener) => listener(theme));
  };

  /* A skin with a fixed default never listens: the OS has no vote there (see above). */
  const unwatch = followsSystem(skin)
    ? watchSystemTheme((systemTheme) => {
        if (chosen) {
          return;
        }
        publish(systemTheme);
      })
    : noop;

  return {
    getTheme: () => theme,
    setTheme: (next: Theme) => {
      chosen = true;
      storeTheme(next);
      publish(next);
    },
    toggleTheme: () => {
      const next: Theme = theme === "dark" ? "light" : "dark";
      chosen = true;
      storeTheme(next);
      publish(next);
    },
    subscribe: (listener: (theme: Theme) => void) => {
      listeners.push(listener);
      return () => {
        const index = listeners.indexOf(listener);
        if (index >= 0) {
          listeners.splice(index, 1);
        }
      };
    },
    stop: () => {
      unwatch();
      listeners.length = 0;
    },
  };
};
